import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  NgZone,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FaDuotoneIconComponent, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendarClock } from '@fortawesome/pro-duotone-svg-icons/faCalendarClock';
import { faChevronLeft } from '@fortawesome/pro-duotone-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-duotone-svg-icons/faChevronRight';
import { faXmark } from '@fortawesome/pro-duotone-svg-icons/faXmark';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ContextMenu } from '../../ui/context-menu/context-menu';
import {
  buildMonthGrid,
  clampMs,
  createDateTimeFormat,
  DateParts,
  DayCell,
  decompose,
  floorQuarterHour,
  formatDisplay,
  formatMonthYear,
  from12h,
  HOURS_12,
  HOURS_24,
  isDayDisabled,
  isHourDisabled,
  isMinuteDisabled,
  MINUTE_STEPS,
  nextQuarterHour,
  pad2,
  partsToMs,
  to12h,
} from './datetime-format';

/** The three time wheels; keys into the settle → setter mapping. */
type WheelKind = 'hour' | 'minute' | 'period';

/** Quiet time after the last scroll event before a wheel counts as settled.
 * Doubles as snap-settle detection: the browser's mandatory-snap correction
 * keeps emitting scroll events until the wheel rests on a snap position. */
const WHEEL_SETTLE_MS = 150;

/**
 * Frosted-glass date + time picker, the in-app replacement for a native
 * `<input type="datetime-local">`. The trigger is a field-styled surface (same
 * accent-ring family as text/select fields) showing a locale-formatted value;
 * clicking it opens a popover — body-portaled and viewport-flipped by
 * {@link ContextMenu}, so it escapes any clipping ancestor (e.g. the
 * create-room dialog's frosted panel) and renders above CDK overlays.
 *
 * The popover holds a localized month calendar and iOS-style time wheels —
 * hour / minute (15-min steps), plus an AM/PM wheel on 12-hour locales. The
 * value resting in the shared center "lens" is the selection: scrolling
 * commits once a wheel settles on a snap position, clicking an entry spins it
 * into the lens. All locale behaviour — clock, week start, weekday/month
 * names, display string — is derived from `Intl` for the active UI language
 * (see `datetime-format.ts`).
 *
 * Value is exchanged as epoch milliseconds via the `value` model. `min`/`max`
 * (epoch ms) grey out and block out-of-range days/times; the create-room dialog
 * uses them to disable the past and keep End ≥ Start.
 */
@Component({
  selector: 'andor-datetime-field',
  standalone: true,
  imports: [TranslatePipe, FontAwesomeModule, FaDuotoneIconComponent, ContextMenu],
  templateUrl: './datetime-field.html',
  styleUrl: './datetime-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatetimeField {
  /** Selected instant as epoch milliseconds, or `null` when empty. */
  readonly value = model<number | null>(null);
  /** Earliest selectable instant (epoch ms); earlier days/times are disabled. */
  readonly min = input<number | null>(null);
  /** Latest selectable instant (epoch ms); later days/times are disabled. */
  readonly max = input<number | null>(null);

  readonly label = input('');
  readonly placeholder = input('');
  readonly disabled = input(false);
  /** Whether a clear (×) affordance appears once a value is set. */
  readonly clearable = input(true);

  protected readonly faCalendarClock = faCalendarClock;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faXmark = faXmark;
  protected readonly minuteSteps = MINUTE_STEPS;

  private readonly _translate = inject(TranslateService);
  private readonly _zone = inject(NgZone);
  // Active UI language as a signal so all `Intl`-derived labels recompute if the
  // user switches language while the picker is mounted.
  private readonly _lang = toSignal(this._translate.onLangChange.pipe(map((e) => e.lang)), {
    // v18: getCurrentLang() is undefined until a language is set — fall back
    // to the configured fallback language for the initial Intl formats.
    initialValue: this._translate.getCurrentLang() ?? this._translate.getFallbackLang() ?? 'en',
  });

  private readonly menu = viewChild.required(ContextMenu);
  private readonly triggerRef = viewChild.required<ElementRef<HTMLElement>>('trigger');
  private readonly hourList = viewChild<ElementRef<HTMLElement>>('hourList');
  private readonly minuteList = viewChild<ElementRef<HTMLElement>>('minuteList');
  private readonly periodList = viewChild<ElementRef<HTMLElement>>('periodList');

  protected readonly open = signal(false);
  // Whatever month the calendar is currently showing.
  protected readonly viewYear = signal(new Date().getFullYear());
  protected readonly viewMonth = signal(new Date().getMonth());
  // Re-stamped on each open so "today" / past-disabling reflect the open moment.
  private readonly _now = signal(Date.now());
  // The value at the moment the popover opened, so Cancel can revert the
  // live-committed selection (day/time clicks commit immediately).
  private _valueOnOpen: number | null = null;
  // Visual selection used while the field is still empty (so the requested
  // "next 15-min slot" default is shown highlighted before the user commits).
  private readonly _preselect = signal<DateParts>(decompose(nextQuarterHour(Date.now())));

  protected readonly fmt = computed(() => createDateTimeFormat(this._lang()));

  // Locale-formatted trigger text, or the placeholder when empty.
  protected readonly displayText = computed(() => {
    const v = this.value();
    if (v == null) {
      this._lang();
      return this.placeholder() || this._translate.instant('Select date & time');
    }
    return formatDisplay(v, this.fmt().locale);
  });

  // Current parts: the committed value when set, else the visual preselect.
  protected readonly selected = computed<DateParts>(() => {
    const v = this.value();
    return v != null ? decompose(v) : this._preselect();
  });
  private readonly committed = computed(() => this.value() != null);

  protected readonly grid = computed(() =>
    buildMonthGrid(this.viewYear(), this.viewMonth(), this.fmt().firstDayOfWeek)
  );
  protected readonly monthLabel = computed(() =>
    formatMonthYear(this.viewYear(), this.viewMonth(), this.fmt().locale)
  );
  protected readonly hourValues = computed(() => (this.fmt().hour12 ? HOURS_12 : HOURS_24));

  private readonly today = computed(() => {
    const d = new Date(this._now());
    return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
  });

  constructor() {
    // One effect per wheel, keyed on the viewChild signals: attaches the scroll
    // machinery when a wheel first mounts (the popover's content is lazily
    // projected on first open and stays in the DOM after), re-attaches if the
    // AM/PM wheel appears/disappears on a 12h ↔ 24h language switch, and
    // detaches on destroy. Element identity survives the context menu's
    // body-portal move, so the listeners persist across opens.
    (
      [
        ['hour', this.hourList],
        ['minute', this.minuteList],
        ['period', this.periodList],
      ] as const
    ).forEach(([kind, list]) =>
      effect((onCleanup) => {
        const el = list()?.nativeElement;
        if (el) onCleanup(this.attachWheel(el, kind));
      })
    );
  }

  // ── Open / close ──────────────────────────────────────────────────────────

  protected toggle(): void {
    if (this.disabled()) return;
    this.menu().toggle(this.triggerRef().nativeElement);
  }

  protected onOpened(): void {
    this.open.set(true);
    this._now.set(Date.now());
    const v = this.value();
    this._valueOnOpen = v;
    const base =
      v != null
        ? decompose(v)
        : decompose(clampMs(nextQuarterHour(this._now()), this.min(), this.max()));
    this.viewYear.set(base.year);
    this.viewMonth.set(base.month);
    if (v == null) this._preselect.set(base);
    // Panel was just portaled + shown; center the active slots in their wheels
    // — instantly (no spin-in flash) and next frame, once layout has settled.
    requestAnimationFrame(() => this.scrollTimeIntoView('auto'));
  }

  protected onClosed(): void {
    this.open.set(false);
  }

  // Footer actions. Selections commit live, so Confirm only needs to ensure an
  // untouched field captures its shown default (next 15-min slot) before
  // closing; Cancel discards live edits by reverting to the on-open value.
  protected confirm(): void {
    if (this.value() == null) this.commit(this.selected());
    this.menu().close();
  }

  protected cancel(): void {
    this.value.set(this._valueOnOpen);
    this.menu().close();
  }

  protected clear(event: MouseEvent): void {
    event.stopPropagation();
    this.value.set(null);
  }

  // ── Calendar ────────────────────────────────────────────────────────────────

  protected isToday(cell: DayCell): boolean {
    const t = this.today();
    return t.year === cell.year && t.month === cell.month && t.day === cell.day;
  }

  protected isSelectedDay(cell: DayCell): boolean {
    if (!this.committed()) return false;
    const s = this.selected();
    return s.year === cell.year && s.month === cell.month && s.day === cell.day;
  }

  protected isDayDisabledCell(cell: DayCell): boolean {
    return isDayDisabled(cell.year, cell.month, cell.day, this.min(), this.max());
  }

  protected selectDay(cell: DayCell): void {
    if (this.isDayDisabledCell(cell)) return;
    const s = this.selected();
    this.commit({
      year: cell.year,
      month: cell.month,
      day: cell.day,
      hour: s.hour,
      minute: s.minute,
    });
  }

  protected canGoPrev(): boolean {
    const lo = this.min();
    if (lo == null) return true;
    const d = new Date(lo);
    return this.viewYear() * 12 + this.viewMonth() > d.getFullYear() * 12 + d.getMonth();
  }

  protected canGoNext(): boolean {
    const hi = this.max();
    if (hi == null) return true;
    const d = new Date(hi);
    return this.viewYear() * 12 + this.viewMonth() < d.getFullYear() * 12 + d.getMonth();
  }

  protected prevMonth(): void {
    if (!this.canGoPrev()) return;
    let m = this.viewMonth() - 1;
    let y = this.viewYear();
    if (m < 0) {
      m = 11;
      y -= 1;
    }
    this.viewMonth.set(m);
    this.viewYear.set(y);
  }

  protected nextMonth(): void {
    if (!this.canGoNext()) return;
    let m = this.viewMonth() + 1;
    let y = this.viewYear();
    if (m > 11) {
      m = 0;
      y += 1;
    }
    this.viewMonth.set(m);
    this.viewYear.set(y);
  }

  // ── Time columns ──────────────────────────────────────────────────────────

  protected hourLabel(hour: number): string {
    return this.fmt().hour12 ? String(hour) : pad2(hour);
  }

  protected minuteLabel(minute: number): string {
    return pad2(minute);
  }

  protected isSelectedHour(hour: number): boolean {
    const s = this.selected();
    return this.fmt().hour12 ? to12h(s.hour).hour === hour : s.hour === hour;
  }

  protected isSelectedMinute(minute: number): boolean {
    return this.selected().minute === minute;
  }

  /** The 24-hour value a column entry maps to, accounting for the active AM/PM. */
  private hour24Of(columnHour: number): number {
    if (!this.fmt().hour12) return columnHour;
    return from12h(columnHour, to12h(this.selected().hour).pm);
  }

  protected isHourDisabledFor(hour: number): boolean {
    const s = this.selected();
    return isHourDisabled(s.year, s.month, s.day, this.hour24Of(hour), this.min(), this.max());
  }

  protected isMinuteDisabledFor(minute: number): boolean {
    const s = this.selected();
    return isMinuteDisabled(s.year, s.month, s.day, s.hour, minute, this.min(), this.max());
  }

  protected setHour(hour: number): void {
    const s = this.selected();
    this.commit({ ...s, hour: this.hour24Of(hour) });
  }

  protected setMinute(minute: number): void {
    this.commit({ ...this.selected(), minute });
  }

  // ── AM/PM (12-hour locales only) ────────────────────────────────────────────

  protected isPm(): boolean {
    return to12h(this.selected().hour).pm;
  }

  protected isPeriodDisabled(pm: boolean): boolean {
    const s = this.selected();
    // The period is selectable if any hour within it has a valid minute slot.
    const hours = pm ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : HOURS_12;
    return !hours.some(
      (h12) => !isHourDisabled(s.year, s.month, s.day, from12h(h12, pm), this.min(), this.max())
    );
  }

  protected setPeriod(pm: boolean): void {
    if (this.isPeriodDisabled(pm)) return;
    const s = this.selected();
    this.commit({ ...s, hour: from12h(to12h(s.hour).hour, pm) });
  }

  // ── Commit ──────────────────────────────────────────────────────────────────

  private commit(parts: DateParts): void {
    let ms = partsToMs(parts);
    const lo = this.min();
    const hi = this.max();
    // Guard against a day switch that carries an out-of-range time: snap to the
    // nearest in-range 15-min slot. The UI already disables invalid slots, so
    // this only fires on the carried-over-time edge.
    if (lo != null && ms < lo) ms = nextQuarterHour(lo);
    if (hi != null && ms > hi) ms = floorQuarterHour(hi);
    this.value.set(ms);
    const d = decompose(ms);
    this.viewYear.set(d.year);
    this.viewMonth.set(d.month);
    // Smooth: this is what spins a clicked entry into the lens. Wheels already
    // resting on the selection get a same-position scrollTo, which is inert.
    requestAnimationFrame(() => this.scrollTimeIntoView('smooth'));
  }

  // ── Wheel scrolling ─────────────────────────────────────────────────────────

  private scrollTimeIntoView(behavior: ScrollBehavior): void {
    this.centerSelected(this.hourList()?.nativeElement, behavior);
    this.centerSelected(this.minuteList()?.nativeElement, behavior);
    this.centerSelected(this.periodList()?.nativeElement, behavior);
  }

  private centerSelected(container: HTMLElement | undefined, behavior: ScrollBehavior): void {
    if (!container) return;
    const target = container.querySelector<HTMLElement>('.is-selected');
    if (target) this.centerSlot(container, target, behavior);
  }

  // Center a slot in its wheel. Scrolls the container itself (rather than
  // scrollIntoView) so a fixed, body-portaled panel can't drag the whole page;
  // the edge spacers make the centered position an exact snap position, so
  // mandatory snap never "corrects" it afterwards.
  private centerSlot(container: HTMLElement, slot: HTMLElement, behavior: ScrollBehavior): void {
    const top = slot.offsetTop - (container.clientHeight - slot.offsetHeight) / 2;
    const reduceMotion =
      typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    container.scrollTo({
      top: Math.max(0, top),
      behavior: reduceMotion ? 'auto' : behavior,
    });
  }

  /**
   * Wire a wheel's scroll-to-select machinery; returns the detach function.
   * Runs outside Angular — per-frame scroll events must not trigger app-wide
   * change detection; only an actual selection change re-enters the zone.
   */
  private attachWheel(el: HTMLElement, kind: WheelKind): () => void {
    return this._zone.runOutsideAngular(() => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      let pointerDown = false;

      const arm = () => {
        if (timer !== null) clearTimeout(timer);
        timer = setTimeout(() => {
          timer = null;
          // A finger resting mid-drag isn't settled — committing now would
          // yank the wheel underneath it; pointerup re-arms.
          if (!pointerDown) this.settleWheel(kind, el);
        }, WHEEL_SETTLE_MS);
      };
      const onPointerDown = () => {
        pointerDown = true;
      };
      // Re-arm on release: a drag let go exactly on a snap position emits no
      // further scroll events, so the release itself must schedule the settle.
      const onPointerUp = () => {
        pointerDown = false;
        arm();
      };
      // Keyboard support: focusing an off-selection entry (Tab) spins it into
      // the lens, and the settle commits it — otherwise the browser's own
      // uncentered focus-scroll would make the settle commit a neighbor.
      const onFocusIn = (event: FocusEvent) => {
        const slot = event.target as HTMLElement;
        if (slot.classList.contains('slot') && !slot.classList.contains('is-selected')) {
          this.centerSlot(el, slot, 'smooth');
        }
      };

      el.addEventListener('scroll', arm, { passive: true });
      el.addEventListener('pointerdown', onPointerDown, { passive: true });
      el.addEventListener('pointerup', onPointerUp, { passive: true });
      el.addEventListener('pointercancel', onPointerUp, { passive: true });
      el.addEventListener('focusin', onFocusIn);

      return () => {
        if (timer !== null) clearTimeout(timer);
        el.removeEventListener('scroll', arm);
        el.removeEventListener('pointerdown', onPointerDown);
        el.removeEventListener('pointerup', onPointerUp);
        el.removeEventListener('pointercancel', onPointerUp);
        el.removeEventListener('focusin', onFocusIn);
      };
    });
  }

  // A wheel came to rest: select the entry nearest the lens. No-ops when that
  // entry is already selected — which is what keeps every programmatic-scroll
  // feedback loop convergent (open-centering, post-commit re-centering,
  // clamp-induced moves, disabled redirects) without any guard flags.
  private settleWheel(kind: WheelKind, el: HTMLElement): void {
    // A closed popover is display: none (scroll state resets to 0); ignore any
    // stray event from the teardown — reopening re-centers in onOpened.
    if (!this.open() || el.clientHeight === 0) return;
    const slots = Array.from(el.querySelectorAll<HTMLButtonElement>('.slot'));
    if (slots.length === 0) return;
    const step = slots.length > 1 ? slots[1].offsetTop - slots[0].offsetTop : slots[0].offsetHeight;
    const center = el.scrollTop + el.clientHeight / 2;
    const first = slots[0].offsetTop + slots[0].offsetHeight / 2;
    const idx = Math.max(0, Math.min(slots.length - 1, Math.round((center - first) / step)));

    if (slots[idx].disabled) {
      // Fallback: disabled slots carry `scroll-snap-align: none`, so native
      // snap normally never rests here. If the wheel still ends up on one
      // (e.g. no snap correction ran), glide to the nearest enabled entry
      // without committing — that scroll's own settle then commits it.
      for (let d = 1; d < slots.length; d++) {
        for (const i of [idx - d, idx + d]) {
          if (i >= 0 && i < slots.length && !slots[i].disabled) {
            this.centerSlot(el, slots[i], 'smooth');
            return;
          }
        }
      }
      return;
    }

    if (this.isWheelIndexSelected(kind, idx)) return;
    this._zone.run(() => this.commitWheelIndex(kind, idx));
  }

  private isWheelIndexSelected(kind: WheelKind, idx: number): boolean {
    switch (kind) {
      case 'hour':
        return this.isSelectedHour(this.hourValues()[idx]);
      case 'minute':
        return this.isSelectedMinute(MINUTE_STEPS[idx]);
      case 'period':
        return this.isPm() === (idx === 1);
    }
  }

  // Route a settled wheel index through the same setters clicks use, so the
  // 12h/24h hour mapping and commit()'s min/max clamping apply identically.
  private commitWheelIndex(kind: WheelKind, idx: number): void {
    switch (kind) {
      case 'hour':
        this.setHour(this.hourValues()[idx]);
        break;
      case 'minute':
        this.setMinute(MINUTE_STEPS[idx]);
        break;
      case 'period':
        this.setPeriod(idx === 1);
        break;
    }
  }
}
